import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Client } from "@/types/client";
import { toast } from "sonner";

interface StatusChangeParams {
  clientId: string;
  newStatus: Client['status'];
}

export const useClientStatusChange = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ clientId, newStatus }: StatusChangeParams) => {
      console.log('🔄 Starting client status update:', { clientId, newStatus });
      
      // التحقق من صحة البيانات
      if (!clientId || !newStatus) {
        throw new Error('Client ID and new status are required');
      }

      // الحصول على بيانات الموظف الحالي
      const currentEmployeeId = localStorage.getItem('currentEmployeeId');
      const currentEmployeeName = localStorage.getItem('currentEmployeeName') || 'مستخدم غير معروف';

      // تحديث حالة العميل
      const { data, error } = await supabase
        .from('clients')
        .update({ 
          status: newStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', clientId)
        .select(`
          *,
          companies (
            id,
            name_ar,
            address_ar,
            mobile
          ),
          client_assignments (
            id,
            role,
            employee_id,
            employees (
              id,
              name
            )
          )
        `)
        .single();

      if (error) {
        console.error('Error updating client status:', error);
        throw error;
      }

      console.log('Client status updated successfully:', data);

      // إنشاء الإشعارات للموظفين المناسبين
      const notifications = [];
      const currentTime = new Date().toISOString();

      // إشعار للمنشئ
      if (data.created_by && data.created_by !== currentEmployeeId) {
        notifications.push({
          employee_id: data.created_by,
          message: `تم تحديث حالة العميل "${data.name_ar}" إلى "${getStatusNameAr(newStatus)}" بواسطة ${currentEmployeeName}`,
          link: `/client-follow-up`,
          is_read: false,
          created_at: currentTime
        });
      }

      // إشعارات للموظفين المعينين
      if (data.client_assignments && data.client_assignments.length > 0) {
        data.client_assignments.forEach((assignment: any) => {
          if (assignment.employee_id && assignment.employee_id !== currentEmployeeId) {
            notifications.push({
              employee_id: assignment.employee_id,
              message: `تم تحديث حالة العميل "${data.name_ar}" إلى "${getStatusNameAr(newStatus)}" بواسطة ${currentEmployeeName}`,
              link: `/client-follow-up`,
              is_read: false,
              created_at: currentTime
            });
          }
        });
      }

      // إرسال الإشعارات
      if (notifications.length > 0) {
        const { error: notificationError } = await supabase
          .from('notifications')
          .insert(notifications);

        if (notificationError) {
          console.error('Error creating notifications:', notificationError);
          // لا نتوقف عند فشل الإشعارات
        } else {
          console.log('Notifications created successfully:', notifications.length);
        }
      }

      // تسجيل النشاط
      const { error: logError } = await supabase
        .from('activity_logs')
        .insert({
          employee_id: currentEmployeeId,
          employee_name: currentEmployeeName,
          activity_type: 'client_status_update',
          module_name: 'clients',
          description: `تم تحديث حالة العميل "${data.name_ar}" من "${getStatusNameAr(data.status)}" إلى "${getStatusNameAr(newStatus)}"`,
          details: {
            client_id: clientId,
            old_status: data.status,
            new_status: newStatus,
            client_name: data.name_ar,
            company_name: data.companies?.name_ar
          },
          created_at: currentTime
        });

      if (logError) {
        console.error('Error creating activity log:', logError);
      }

      return data;
    },
    onSuccess: (data) => {
      // تحديث cache للعملاء
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      
      // تحديث cache للإشعارات
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      
      toast.success(`تم تحديث حالة العميل إلى "${getStatusNameAr(data.status)}" بنجاح`);
    },
    onError: (error: any) => {
      console.error('Status update failed:', error);
      
      if (error.message?.includes('permission denied') || error.message?.includes('policy')) {
        toast.error("ليس لديك الصلاحية لتحديث حالة هذا العميل");
      } else {
        toast.error("فشل في تحديث حالة العميل. يرجى المحاولة مرة أخرى");
      }
    },
  });
};

// دالة مساعدة لترجمة حالات العملاء للعربية
function getStatusNameAr(status: string): string {
  const statusMap: Record<string, string> = {
    'waiting': 'عميل جديد',
    'pricing_requested': 'طلب تسعير',
    'pricing_sent': 'تم إرسال السعر',
    'booking_confirmed': 'تأكيد الحجز',
    'traveling': 'قيد السفر',
    'customer_service': 'خدمة العملاء',
    'confirmed': 'مؤكد',
    'in_progress': 'قيد المعالجة',
    'waiting_supplier': 'انتظار المورد',
    'cancelled': 'ملغي'
  };
  
  return statusMap[status] || status;
}